import { useEffect, useRef, useState } from "react";
import ReportHubLogo from "./logo/ReportHubLogo";

type Props = {
  isLoading: boolean;
  onComplete: () => void;
  minLoopDurationMs?: number;
};

const ZOOM_DURATION_MS = 950;
const FADE_DURATION_MS = 220;

const easeOutQuart = (t: number) => 1 - Math.pow(1 - t, 4);

const AppIntroLoading = ({
  isLoading,
  onComplete,
  minLoopDurationMs = 1500,
}: Props) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<SVGSVGElement>(null);
  const mountTimeRef = useRef<number>(Date.now());
  const animationStartedRef = useRef<boolean>(false);
  const [phase, setPhase] = useState<"loop" | "zoom">("loop");

  useEffect(() => {
    if (animationStartedRef.current) return;
    if (isLoading) return;

    const elapsed = Date.now() - mountTimeRef.current;
    const wait = Math.max(0, minLoopDurationMs - elapsed);
    const timer = window.setTimeout(startReveal, wait);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, minLoopDurationMs]);

  const startReveal = () => {
    if (animationStartedRef.current) return;
    animationStartedRef.current = true;

    const overlay = overlayRef.current;
    const svg = logoRef.current;
    if (!overlay || !svg) {
      onComplete();
      return;
    }

    const rect = svg.getBoundingClientRect();
    const sx = rect.width / 310;
    const sy = rect.height / 90;
    const cx = rect.left + 119 * sx;
    const cy = rect.top + 51 * sy;
    const startR = 12 * Math.min(sx, sy);

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const endR =
      Math.max(
        Math.hypot(cx, cy),
        Math.hypot(vw - cx, cy),
        Math.hypot(cx, vh - cy),
        Math.hypot(vw - cx, vh - cy)
      ) + 4;

    setPhase("zoom");

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (reducedMotion) {
      overlay.style.opacity = "0";
      window.setTimeout(onComplete, FADE_DURATION_MS);
      return;
    }

    const tStart = performance.now();
    const tick = (now: number) => {
      if (!overlayRef.current) return;
      const t = Math.min((now - tStart) / ZOOM_DURATION_MS, 1);
      const e = easeOutQuart(t);
      const r = startR + (endR - startR) * e;
      const mask = `radial-gradient(circle at ${cx}px ${cy}px, transparent ${r}px, black ${
        r + 1
      }px)`;
      overlay.style.maskImage = mask;
      overlay.style.webkitMaskImage = mask;
      if (t < 1) {
        requestAnimationFrame(tick);
      } else {
        overlay.style.opacity = "0";
        window.setTimeout(onComplete, FADE_DURATION_MS);
      }
    };
    requestAnimationFrame(tick);
  };

  return (
    <div
      ref={overlayRef}
      className={`app-intro-overlay${
        phase === "zoom" ? " app-intro-overlay--zoom" : ""
      }`}
      role="status"
      aria-live="polite"
      aria-label="Loading Report Hub"
    >
      <div className="app-intro-stack">
        <ReportHubLogo
          ref={logoRef}
          variant="loop"
          className="app-intro-logo"
        />
        <div className="app-intro-label">Loading</div>
      </div>
    </div>
  );
};

export default AppIntroLoading;
